import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {Observable, throwError} from 'rxjs';
import {catchError} from 'rxjs/operators';

import {AuthenticationService} from '@auth/authentication.service';
import {LoginModel} from '@models/login.model';
import {LoginAdminModel} from '@models/login-admin.model';
import {environment} from '@env/environment';
import {LocalStorageService} from '@services/local-storage.service';
import {TranslateService} from '@ngx-translate/core';
import {NotificationService} from '@notifications';
import {Router} from '@angular/router';
import {LoginTokenModel} from '@models/login-token.model';
import {AuthenticationHelperService} from '@auth/authentication-helper.service';
import {CUSTOMER_RELATED} from '@auth/constants';
import {afterSignInUrl} from '@auth/sign-in-url';
import {LedgerSyncModalService} from '@shared/ledger-sync-modal/service.service';
import {ConfirmationPopupService} from '@services/confirmation-popup.service';
import {ClientManagementService} from '@services/client-management.service';
import {useSpinner} from '@spinner';
import {CustomerService} from '@services/customer-service';
import {AutoLogoutService} from '../../controls/auto-logout/autologout.service';
import {TaskCenterRealtimeService} from '../../pages/task-center/task-center-realtime.service';

/**
 * Implementation of AuthenticationService
 */
@Injectable()
export class JwtAuthenticationService implements AuthenticationService {

  constructor(
    private http: HttpClient,
    private localStorage: LocalStorageService,
    private translate: TranslateService,
    private notificationService: NotificationService,
    private router: Router,
    private authenticationHelperService: AuthenticationHelperService,
    private ledgerSyncModalService: LedgerSyncModalService,
    private confirmationPopupService: ConfirmationPopupService,
    private clientManagementService: ClientManagementService,
    private customerService: CustomerService,
    private autoLogoutService: AutoLogoutService,
    private taskCenterRealtimeService: TaskCenterRealtimeService,
  ) {
  }

  /**
   * check if user is authenticate
   * @param loginData {LoginModel}
   */
  authenticate(loginData: LoginModel): Observable<any> {
    return this.http.post(environment.PUBLIC_API_URL + '/authenticate', loginData)
    .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  authenticateWithToken(loginData: LoginTokenModel): Observable<any> {
    return this.http.post(environment.PUBLIC_API_URL + '/authenticate-token', loginData)
    .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  /**
   * Authenticate admin user
   * @param loginData {LoginAdminModel}
   */
  authenticateAdmin(loginData: LoginAdminModel): Observable<any> {
    return this.http.post(environment.PUBLIC_API_URL + '/authenticate-admin', loginData)
    .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  /**
   * Check if current user is authenticated
   */
  isAuthenticated(): Observable<any> {
    return this.http.get(environment.PUBLIC_API_URL + '/verify-authentication')
    .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  /**
   * Logout for current user
   */
  logout() {
    return this.authenticationHelperService.logout();
  }

  /**
   * Handle successful sign in response from server
   */
  handleSignInSuccess(data: any, returnUrl?: string) {
    this.localStorage.setCurrentUserDetails({
      userId: data.userId || data.id,
      role: data.role,
      permissions: data.permissions,
      username: data.username,
      fullName: data.fullName,
      companyName: data.companyName,
      isAdminLogin: data.isAdminLogin,
      isCompanyOwner: data.isCompanyOwner,
      validated: data.validated,
      logoUrl: data.logoUrl,
      isGroupsEnabled: data.isGroupsEnabled,
      twofaEnabled: data.twofaEnabled,
      rememberMe: data.rememberMe === true,
    });

    this.router.navigateByUrl(afterSignInUrl(returnUrl));
    this.translate.setDefaultLang(environment.DEFAULT_LANG);
    this.localStorage.setLang(this.localStorage.getLang() || environment.DEFAULT_LANG);

    if (CUSTOMER_RELATED.includes(data.role)) {
      // no consent for now as well
      // setTimeout(() => {
      //   this.http.get<{ hasChatGPTConsent: boolean }>(environment.API_URL + '/settings/chatgpt-consent-status')
      //   .subscribe((result) => {
      //     if (!result.hasChatGPTConsent) {
      //       this.showChatGPTConsent();
      //     }
      //   });
      // }, 5000);
      // setTimeout(() => {
      //   this.http.get<{ hasUnmappedClients: boolean }>(environment.API_URL + '/quickbooks/has-unmapped-clients')
      //   .subscribe((result) => {
      //     if (result.hasUnmappedClients) {
      //       this.askToMapClients();
      //     }
      //   });
      // }, 10000);
    }

    if (data.logoUrl) {
      this.customerService.logo.next({logoUrl: data.logoUrl});
    }

    this.autoLogoutService.start();
    this.taskCenterRealtimeService.connect();
  }

  showChatGPTConsent() {
    const callback = (modalRef, consent) => {
      modalRef.hide();
      this.http.put(environment.API_URL + '/settings/chatgpt-consent', {consent})
      .subscribe();
    };
    this.ledgerSyncModalService.show({
      header: this.translate.instant('Enable ChatGPT API Data Processing') + '?',
      headerAlign: 'center',
      html: `
<p>${this.translate.instant('To enhance our services, we would like to use the ChatGPT API to securely process and extract relevant information from your banking data')}. ${this.translate.instant('Please note')}:</p>

<ul class="list-disc d-flex flex-column gap-1">
    <li><b>${this.translate.instant('Security')}</b>: ${this.translate.instant('The ChatGPT API is SOC 2 compliant, ensuring that all data interactions are encrypted, securely transmitted, and adhere to strict security standards')}.</li>
    <li><b>${this.translate.instant('Privacy')}</b>: ${this.translate.instant('Your data is processed in a secure environment, and no personal information is stored or retained beyond the immediate processing need')}.</li>
    <li><b>${this.translate.instant('Transparency')}</b>: ${this.translate.instant('We use this data solely for the purpose of providing accurate and efficient service')}. ${this.translate.instant('You have complete control over this feature and can choose to enable or disable it at any time')}.</li>
</ul>

<p class="text-center">${this.translate.instant('For more information, please visit')} <br /><a href="https://openai.com/security-and-privacy/" target="_blank" class="link-primary">${this.translate.instant('ChatGPT Security and Privacy page')}</a>.</p>
<p class="fw-500 text-center">${this.translate.instant('Would you like to enable ChatGPT API data processing')}?</p>`,
      footer: true,
      buttons: [
        {
          caption: this.translate.instant('Yes, enable'),
          callback: (modalRef) => callback(modalRef, true),
        },
        {
          caption: this.translate.instant('No, thank you'),
          variant: 'danger',
          callback: (modalRef) => callback(modalRef, false),
        },
      ],
    })
  }

  // askToMapClients() {
  //   this.confirmationPopupService.confirm(
  //     'Confirm Action',
  //     'You have QBO clients with unmapped categories. Do you want to map them now?',
  //     'Yes', 'No'
  //   ).subscribe((yes) => {
  //     if (yes) {
  //       // can not redirect without knowledge that at least one qbo-connected client presented in account
  //       this.clientManagementService.getByIntegration('qbo')
  //       .pipe(useSpinner())
  //       .subscribe(() => this.router.navigate(['/quickbooks/global-categories-settings']));
  //     }
  //   });
  // }

  /**
   * Handle error response from server
   */
  handleSignInError(error) {
    this.localStorage.setValue('username', error.username);
    let errorMessage = '';
    switch (error.code) {
      case 426:
        // case: uncompleted user registration (finicityId and zohoId are empty)
        this.notificationService.success('Please complete registration');
        //this.router.navigate(['/sign-up/plans/' + error.username]);
        this.router.navigate(['/sign-up/order']);
        break;
      case 417:
        // case: uncompleted user registration (we have userId and zohoId, but finicityId is empty)
        errorMessage = error.message;
        this.router.navigate(['/registration/complete']);
        break;
      case 412: // case: uncompleted user registration (we have finicityId, but zohoId is empty)
      // case: issue with finicity or username not found
      case 404:
      case 403:
      default:
        errorMessage = error.message;
        break;
    }
    return errorMessage;
  }

}
